/**
 * Breadcrumb Configuration
 * Single source of truth for all breadcrumb labels
 * 
 * Rules:
 * - Use absolute paths (start with /)
 * - Match your route definitions exactly
 * - Include all parent paths
 * - For dynamic segments, use the pattern path or a function
 */

export interface BreadcrumbConfig {
  [path: string]: string | ((params?: any) => string);
}

export const breadcrumbConfig: BreadcrumbConfig = {
  // Dashboard
  '/dashboard': 'Dashboard',
  
  // Administration Module (PHASE 1 - PILOT)
  '/administration': 'Administration',
  '/administration/department': 'Department',
  '/administration/position': 'Position',
  '/administration/user': 'User',
  '/administration/access': 'Access Permission',
  '/administration/audit': 'Audit Trail',
  '/administration/audit/new': 'Add new',
  
  // Design Module (Phase 2)
  '/design': 'Design',
  '/design/lib-profile': 'Library Profile',
  '/design/lib-profile/record-profile-design': 'Library Design',
  // '/design/lib-profile/new-profile': 'Library Design',
  // '/design/lib-profile/new-profile/:id': (params) => `Profile ${params?.id || ''}`,
  '/design/lib-profile/fields-lookup': 'Selection Group',
  '/design/lib-profile/fields-lookup/edit': 'Edit Selection Group',
  '/design/lib-profile/fields-lookup/edit/:code': 'Edit Selection Group',
  '/design/lib-profile/new-fields-lookup': 'New Selection Group',
  '/design/lib-profile/master-data-lookup': 'Master Data Lookup',
  '/design/lib-profile/master-data-lookup/edit': 'Edit Data Lookup',
  '/design/lib-profile/document-type': 'Document Type',
  '/design/lib-profile/library-access-setting': 'Library Access Permission',
  '/design/lib-profile/library-access-setting/create': 'Create Library Access Permission',
  '/design/lib-profile/library-access-setting/:code': 'Edit Library Access Permission',
  // '/design/process': 'Process',
  // '/design/process/process-list': 'Process Design',
  '/design/process/new-process': 'Process Design',
  '/design/process/new-process/:id': (params) => params?.id === "1" ? 'Process Design' : 'Process Design',
  '/design/reward-index-setting': 'Reward Index Setting',
  '/design/teams': 'Teams',
  '/design/reminder': 'Reminder',
  
  // Library Module (Phase 2)
  '/library': 'Library',
  '/library/:libraryCode': (params) => params?.libraryCode ? `${params.libraryCode}` : 'Library',
  // Dynamic routes handled in component with manual breadcrumbs
  
  // Process Module (Phase 3)
  '/process/process-access': 'Process Access',
  // '/process/process-access/:processCode/tasks': 'Task Board', // Now handled manually in TaskBoard component
  // '/process/process-access/:processCode/tasks/:transactionNo': 'Task Details', // Now handled manually in LibraryRecordView component
  '/process/process-access/process-access-permission': 'Process Access Setting',
  '/process/process-access/process-access-permission/create': 'Create Access Permission',
  '/process/process-access/process-access-permission/:code': 'Edit Access Permission',
  
  // My Tasks Module (Phase 3)
  '/my-tasks': 'My Tasks',
  '/my-tasks/my-tasks-listing': 'My Tasks Listing',
  
  // Add more routes as you migrate...
};

/**
 * Helper to get breadcrumb label for a given path
 * Supports dynamic functions for parameterized routes
 * 
 * @param path - The route path
 * @param params - Optional parameters for dynamic routes
 * @returns The breadcrumb label
 */
export function getBreadcrumbLabel(
  path: string, 
  params?: any
): string {
  const config = breadcrumbConfig[path];
  
  // If config is a function, call it with params
  if (typeof config === 'function') {
    return config(params);
  }
  
  // If config is a string, return it
  if (typeof config === 'string') {
    return config;
  }
  
  // Fallback: capitalize and clean up the last segment
  const segments = path.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  
  // Handle kebab-case and snake_case
  return lastSegment
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
